/**
 * Wave 24u.3 — Parent view-mode context.
 *
 * Lets an admin/coach/staff user who ALSO owns players toggle their
 * viewing experience between their "real" role and Parent. The mode
 * is persisted per-device in localStorage and attached to every API
 * request via the `X-View-Mode` header (see services/api.js).
 *
 * Contract with the backend:
 *   • `GET /api/auth/me/eligibility` returns `can_switch_to_parent`,
 *     `player_count`, `effective_role`, and `view_mode`.
 *   • The backend only honours `X-View-Mode: parent` for users who
 *     have ≥1 linked player in the tenant. Otherwise the header is
 *     silently ignored, so a stale/mistaken localStorage value cannot
 *     escalate access.
 *
 * Frontend contract:
 *   • `viewMode` is either `null` (real role) or `'parent'`.
 *   • Real parents never see the toggle (`can_switch_to_parent=false`).
 *   • Toggle default state at first login is real role (q5-a).
 *   • Coach features are hidden entirely while in parent view (q6-a) —
 *     enforced by the sidebar/route guards, not this context.
 */
import {
  createContext, useContext, useState, useEffect, useCallback, useMemo,
} from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const ViewModeContext = createContext(null);

export const useViewMode = () => {
  const ctx = useContext(ViewModeContext);
  if (!ctx) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return ctx;
};

const readStoredMode = () => {
  try {
    const v = localStorage.getItem('viewMode');
    return v === 'parent' ? 'parent' : null;
  } catch { return null; }
};

const writeStoredMode = (mode) => {
  try {
    if (mode === 'parent') localStorage.setItem('viewMode', 'parent');
    else localStorage.removeItem('viewMode');
  } catch { /* localStorage unavailable — ignore */ }
};

export const ViewModeProvider = ({ children }) => {
  const { user, token, loading: authLoading } = useAuth();
  const [viewMode, setViewModeState] = useState(readStoredMode);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(false);

  // Refresh eligibility from the backend whenever the authenticated
  // user changes (login / logout / role change). Anonymous visitors
  // skip the call — the endpoint requires auth.
  useEffect(() => {
    let cancelled = false;
    // Wait until AuthContext finishes resolving before deciding anything.
    // On a full-page reload (e.g. right after clicking the parent-mode
    // switcher, which does window.location.href='/parent') the token is
    // read from storage synchronously but `user` is briefly null while
    // /auth/me is in flight. Treating that transient null as "signed out"
    // used to WIPE the freshly-stored parent view mode, so the user was
    // silently bounced back to their admin dashboard. Guarding on
    // authLoading prevents that race.
    if (authLoading) return () => { cancelled = true; };
    if (!user || !token) {
      setEligibility(null);
      // Clear stale mode when signed out so a subsequent login
      // starts in the real role (q5-a).
      if (readStoredMode() === 'parent') {
        writeStoredMode(null);
        setViewModeState(null);
      }
      return () => { cancelled = true; };
    }
    setLoading(true);
    api.get('/auth/me/eligibility')
      .then((res) => {
        if (cancelled) return;
        setEligibility(res.data);
        // If localStorage claims parent-mode but the user is no longer
        // eligible (lost their player, role changed, etc.), reset it.
        if (readStoredMode() === 'parent' && !res.data?.can_switch_to_parent) {
          writeStoredMode(null);
          setViewModeState(null);
        }
      })
      .catch(() => {
        if (!cancelled) setEligibility(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, token, authLoading]);

  const setViewMode = useCallback((next) => {
    const normalized = next === 'parent' ? 'parent' : null;
    writeStoredMode(normalized);
    setViewModeState(normalized);
    // Full reload so route guards, sidebar, and any cached page state
    // re-render under the new mode. Simplest correctness guarantee.
    try { window.location.href = normalized === 'parent' ? '/parent' : '/'; }
    catch { /* SSR — no-op */ }
  }, []);

  const value = useMemo(() => ({
    viewMode,                                       // 'parent' | null
    isParentMode: viewMode === 'parent',
    effectiveRole: viewMode === 'parent'
      ? 'parent'
      : (eligibility?.role || user?.role || null),
    realRole: eligibility?.role || user?.role || null,
    canSwitchToParent: !!eligibility?.can_switch_to_parent,
    playerCount: eligibility?.player_count ?? 0,
    loading,
    setViewMode,
  }), [viewMode, eligibility, user, loading, setViewMode]);

  return (
    <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>
  );
};

export default ViewModeContext;
